import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Trash2, Image as ImageIcon, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { confirmAction } from './ConfirmModal';

interface MediaAsset {
  id: string;
  storeId: string;
  url: string;
  publicId: string;
  resourceType: string;
  bytes: number;
  createdAt: string;
}

interface MediaManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  folder?: string;
}

export const MediaManagerModal: React.FC<MediaManagerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  folder = 'general',
}) => {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      // Endpoint handled by the backend uploads routes under /api/v1/uploads
      const res = await apiClient.get<any>('/uploads');
      if (res?.data) {
        setAssets(res.data);
      } else if (Array.isArray(res)) {
        setAssets(res);
      }
    } catch (err) {
      console.error('Failed to fetch media:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMedia();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    setUploading(true);
    try {
      const res = await apiClient.post<any>('/uploads/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newAsset = res?.data;
      if (newAsset) {
        setAssets([newAsset, ...assets]);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // prevent selection
    const confirmed = await confirmAction('Are you sure you want to delete this media asset?');
    if (!confirmed) return;

    try {
      await apiClient.delete(`/uploads/${id}`);
      setAssets(assets.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete image.');
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return createPortal(
    <div className="fixed inset-0 z-[99990] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="w-full max-w-4xl bg-white border border-slate-200 shadow-2xl rounded-lg overflow-hidden flex flex-col max-h-[85vh] animate-scaleUp">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#006838]/10 rounded-md">
              <ImageIcon className="w-5 h-5 text-[#006838]" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800 text-lg">Media Library</h2>
              <p className="text-xs text-slate-500 font-medium">Select or upload images for your store</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between bg-white">
          <div className="text-sm text-slate-500 font-medium">
            {assets.length} item{assets.length !== 1 ? 's' : ''}
          </div>
          
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
          />
          <button
            onClick={handleUploadClick}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-[#006838] hover:bg-[#004d2a] text-white font-semibold text-sm rounded-md shadow-sm transition-all disabled:opacity-70"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : 'Upload Image'}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-48 space-y-3">
              <Loader2 className="w-8 h-8 text-[#006838] animate-spin" />
              <p className="text-sm font-medium text-slate-500">Loading media...</p>
            </div>
          ) : assets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-slate-200 rounded-xl bg-white space-y-4">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-slate-400" />
              </div>
              <div className="text-center">
                <h3 className="font-semibold text-slate-700">No media found</h3>
                <p className="text-sm text-slate-500 mt-1 max-w-sm">Upload images to use them in your store's theme and content.</p>
              </div>
              <button
                onClick={handleUploadClick}
                className="px-4 py-2 border border-slate-300 hover:border-[#006838] hover:text-[#006838] text-slate-600 font-medium text-sm rounded-md shadow-sm transition-all bg-white"
              >
                Upload your first image
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => onSelect(asset.url)}
                  className="group relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-[#006838]/50 transition-all cursor-pointer flex items-center justify-center"
                >
                  <img
                    src={asset.url}
                    alt="Media asset"
                    className="object-cover w-full h-full"
                    loading="lazy"
                  />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                    <span className="px-3 py-1 bg-white text-slate-900 font-bold text-xs rounded-full shadow-lg">
                      Select
                    </span>
                  </div>

                  {/* Actions (always visible on hover, but interactive) */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => handleDelete(e, asset.id)}
                      className="p-1.5 bg-white/90 hover:bg-red-50 text-red-600 rounded-md shadow-sm transition-colors"
                      title="Delete image"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* File Info */}
                  <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                    <div className="text-[10px] text-white/90 font-medium truncate">
                      {formatBytes(asset.bytes)} • {asset.resourceType}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
